// ImageryProvider.js — Ticket 02: satellite imagery layer (NASA GIBS).
//
// Plain, framework-independent contract that any "give me a raster imagery layer for this
// bbox + DTG" data source must satisfy. No OL/React/network imports here — this module is
// just the shape + a runtime shape-check, so both the coverage-decision logic
// (`shouldShowImagery.js`) and MapEditorModal's wiring can depend on the *interface* rather
// than a concrete provider. `gibsImageryProvider.js` is the (currently only) implementation.
//
// -- Shape ---------------------------------------------------------------------------------
//
//   /**
//    * @typedef {[number, number, number, number]} Bbox
//    *   [minLon, minLat, maxLon, maxLat] in EPSG:4326 (lon/lat degrees) — i.e. the same
//    *   lon/lat-corner shape `MapEditorModal.jsx` already produces via `toLonLat`/
//    *   `bboxFromOlMapRect` for view/AOI bounds.
//    */
//
//   /**
//    * @typedef {object} ImageryCoverage
//    * @property {string|null} tileUrlTemplate
//    *   An OL `source/XYZ`-compatible URL template (`{z}`/`{x}`/{y}` placeholders) for the
//    *   given bbox+dtg, or `null` when `hasCoverage` is false (nothing to render).
//    * @property {boolean} hasCoverage
//    *   Whether this provider has usable imagery for the given bbox+dtg at all. Callers
//    *   (MapEditorModal) use this — and *only* this — to decide whether to show the imagery
//    *   layer or fall back to the default basemap; there is no separate zoom-gating.
//    */
//
//   /**
//    * @typedef {object} ImageryProvider
//    * @property {string} id
//    *   Stable identifier for the provider (useful for logging/telemetry, layer `id`s, etc).
//    * @property {(bbox: Bbox|null, dtg: number|Date|import('dayjs').Dayjs) => ImageryCoverage} getCoverage
//    *   The core contract: given a viewport/AOI bbox (or `null` if unknown/unavailable — a
//    *   global-coverage provider like GIBS may still answer meaningfully) and a DTG, return
//    *   whether imagery is available and, if so, the tile URL template to render it with.
//    *   Must be synchronous and side-effect-free (no network calls) so it's cheap to call on
//    *   every DTG-cursor tick / viewport move.
//    * @property {(bbox?: Bbox|null) => { start: number, end: number }} getAvailableDateRange
//    *   Lists the available-date range for an AOI, as epoch-ms bounds. Providers with
//    *   effectively global, daily coverage (like GIBS) may return the same fixed range
//    *   regardless of `bbox` — see `gibsImageryProvider.js` for how/why that's a reasonable
//    *   stub rather than a live per-AOI capabilities probe.
//    */

const REQUIRED_METHODS = ['getCoverage', 'getAvailableDateRange'];

/**
 * Runtime shape-check for an `ImageryProvider`. Throws a descriptive `TypeError` if `provider`
 * doesn't satisfy the contract above. Useful as a cheap guard at provider-construction time
 * (e.g. at the bottom of `gibsImageryProvider.js`) or wherever a provider is accepted from
 * outside code that might not respect the JSDoc-only contract.
 *
 * @param {unknown} provider
 * @returns {import('./ImageryProvider').ImageryProvider} `provider`, unchanged, for chaining.
 */
export function assertImageryProvider(provider) {
    if (!provider || typeof provider !== 'object') {
        throw new TypeError('ImageryProvider: expected an object, got ' + typeof provider);
    }
    for (const method of REQUIRED_METHODS) {
        if (typeof provider[method] !== 'function') {
            throw new TypeError(`ImageryProvider: missing required method "${method}"`);
        }
    }
    if (typeof provider.id !== 'string' || !provider.id) {
        throw new TypeError('ImageryProvider: missing required non-empty string "id"');
    }
    return provider;
}
